// ═══════════════════════════════════════════════════════════════════════════
// PAGE: WORKSPACE — tuiles d'organisation du travail personnel
// Cf. docs/specs/workspace_tuiles.md
// ═══════════════════════════════════════════════════════════════════════════
//
// Ce n'est PAS la page d'accueil : celle-ci reste la liste des actions. Le
// Workspace se rejoint en un clic depuis la pastille de navigation.
//
// La route s'appelle `workspace` et non `dashboard` : `pages.dashboard` est
// déjà pris par la liste des projets (nom historique trompeur, non corrigé
// ici).

import type { CatalogEntry, LayoutItem, WorkspaceTile } from '../api/workspace.js';
import { facetForKpi, landingHash as buildLandingHash } from '../domain/workspace-landing.js';
import { initDragOrder } from '../utils/drag-order.js';
import * as store from './workspace-store.js';
import { renderTile, tileTitle } from './workspace-tiles.js';

let reorderMode = false;

// ── Atterrissage ───────────────────────────────────────────────────────────

/**
 * URL d'atterrissage d'une tuile, ou de l'un de ses compteurs.
 *
 * Quand la tuile publie un atterrissage propre au compteur cliqué
 * (`landings[<clé>]`), il gagne : les trois compteurs d'Hygiène comptent
 * trois populations différentes et ne peuvent pas partager une seule liste.
 * La logique de sérialisation reste pure et vit dans `domain/`.
 */
export function landingHash(
  tile: WorkspaceTile,
  extra: Record<string, string> = {},
  counter?: string,
): string {
  const landing = (counter && tile.landings?.[counter]) || tile.landing;
  return buildLandingHash(landing, extra);
}

// ── Rendu ──────────────────────────────────────────────────────────────────

function renderGrid(): string {
  const tiles = store.getTiles();
  if (!tiles.length) {
    return `<div class="ws-onboard">
      <p>${esc(t('workspace.empty'))}</p>
      <button type="button" class="btn btn-primary" id="ws-add">${esc(t('workspace.add'))}</button>
    </div>`;
  }
  return `<div class="ws-grid${reorderMode ? ' ws-reorder' : ''}">${tiles
    .map((tile) =>
      renderTile(tile, 'full', { reorder: reorderMode, flash: store.isFlashing(tile.id) }),
    )
    .join('')}</div>`;
}

/**
 * En-tête de la page.
 *
 * « Réinitialiser » n'apparaît QUE pendant la réorganisation : c'est une
 * action destructrice pour la disposition, elle n'a pas sa place à portée de
 * clic le reste du temps.
 */
function header(): string {
  const reset = reorderMode
    ? `<button class="btn btn-secondary" id="ws-reset" style="margin-left:8px">` +
      `${esc(t('workspace.reset'))}</button>`
    : '';
  return (
    `<button class="btn btn-secondary" id="ws-reorder" aria-pressed="${reorderMode}">` +
    `${esc(reorderMode ? t('workspace.reorderDone') : t('workspace.reorder'))}</button>` +
    reset +
    `<button class="btn btn-primary" id="ws-add" style="margin-left:8px">${esc(t('workspace.add'))}</button>`
  );
}

function paint(): void {
  setHeader(t('workspace.title'), header());
  setContent(renderGrid());
  wire();
}

// ── Persistance ────────────────────────────────────────────────────────────

/**
 * Enregistre la disposition. Les `id` sont renvoyés : sans eux, un simple
 * réordonnancement renumérote les instances et casse le rafraîchissement
 * ciblé du store.
 */
async function persist(tiles: WorkspaceTile[]): Promise<void> {
  const previous = store.getTiles();
  store.setTiles(tiles); // optimiste
  // `id` n'est envoyé que pour une tuile DÉJÀ posée. Une création porte
  // l'id sentinelle 0 côté front (cf. `newTile`) : l'émettre ferait chercher
  // au serveur une instance inexistante, donc un 403 sur tout ajout.
  const payload: LayoutItem[] = tiles.map((tj) =>
    tj.id > 0
      ? { id: tj.id, tile_key: tj.key, size: tj.size, params: tj.params || {} }
      : { tile_key: tj.key, size: tj.size, params: tj.params || {} },
  );
  try {
    const res = await api.putWorkspaceTiles(payload);
    store.setTiles(res.tiles);
  } catch (e) {
    // Jamais de disposition silencieusement perdue : on restaure et on le dit.
    store.setTiles(previous);
    safeWarn('[workspace] persist', e);
    alert(t('workspace.saveFailed'));
  }
  paint();
}

function move(tileId: number, dir: 'up' | 'down'): void {
  const tiles = [...store.getTiles()];
  const i = tiles.findIndex((tj) => tj.id === tileId);
  const j = dir === 'up' ? i - 1 : i + 1;
  if (i < 0 || j < 0 || j >= tiles.length) return;
  const a = tiles[i];
  const b = tiles[j];
  if (!a || !b) return;
  tiles[i] = b;
  tiles[j] = a;
  void persist(tiles);
}

/**
 * Applique l'ordre issu d'un glisser-déposer.
 *
 * Un id inconnu ferait disparaître une tuile de la disposition enregistrée :
 * on abandonne plutôt que de persister une liste amputée.
 */
function reorderTo(ids: number[]): void {
  const byId = new Map(store.getTiles().map((tj) => [tj.id, tj]));
  const next = ids.map((id) => byId.get(id)).filter((tj): tj is WorkspaceTile => tj !== undefined);
  if (next.length !== byId.size) return;
  void persist(next);
}

/**
 * Repose la disposition d'origine.
 *
 * Elle se reconstruit depuis le catalogue (`default`), et non depuis un
 * endpoint de réinitialisation : le serveur ne sème sa disposition par défaut
 * qu'une seule fois dans la vie du compte (`workspace_seeded_at`), justement
 * pour qu'un Workspace vidé le reste. Les tuiles paramétrables en sont
 * exclues, comme au premier affichage — leur paramètre ne se devine pas.
 *
 * Confirmation demandée : le geste efface des tuiles ajoutées à la main et
 * leurs paramètres, et il n'a pas d'annulation.
 */
function resetLayout(): void {
  const defaults = store.getCatalog().filter((c) => c.default && !c.params_schema.length);
  if (!defaults.length) return;
  openModal(
    t('workspace.reset'),
    `<p>${esc(t('workspace.resetConfirm'))}</p>`,
    () => {
      closeModal();
      return persist(defaults.map((entry) => newTile(entry, {})));
    },
    t('workspace.reset'),
  );
}

function remove(tileId: number): void {
  void persist(store.getTiles().filter((tj) => tj.id !== tileId));
}

// ── Popups ─────────────────────────────────────────────────────────────────

/** « ? » — ce que compte la tuile, ce qu'elle exclut, pourquoi elle peut être vide. */
function openHelp(tileKey: string): void {
  openModal(
    tileTitle({ key: tileKey } as WorkspaceTile),
    `<p>${esc(t('workspace.tile.' + tileKey + '.help'))}</p>`,
    () => {},
    t('common.close'),
  );
}

/** Libellé tronqué : deux colonnes étroites, les noms longs se chevauchent. */
function truncate(text: string, max = 20): string {
  return text.length > max ? text.slice(0, max - 1) + '…' : text;
}

/**
 * Formulaire de paramètres — même popup à l'ajout et au réglage.
 *
 * Générique sur le schéma déclaré par la tuile : projet piloté pour
 * « Qualité », équipe dirigée pour « Hygiène équipe ». Les valeurs proposées
 * viennent du serveur (`schema.options`), jamais d'un appel séparé — le
 * sélecteur montre alors exactement ce que l'enregistrement accepte.
 *
 * Présentation reprise du bloc « Projets liés » de la fiche action (recherche
 * + deux colonnes, code en gris devant le nom), mais en choix UNIQUE : une
 * tuile ne porte qu'un objet. D'où des `radio` et non des cases à cocher.
 *
 * `picked` est la source de vérité, jamais le DOM : la liste est re-rendue à
 * chaque frappe dans la recherche, et lire l'état coché ferait perdre le choix
 * dès qu'il sort du filtre (c'est le bug d'avril 2026 sur le picker de la
 * fiche action, à ne pas refaire ici).
 */
function openParams(entry: CatalogEntry, existing?: WorkspaceTile): void {
  const schema = entry.params_schema[0];
  if (!schema) return;
  const initial = existing?.params?.[schema.name];
  let picked = typeof initial === 'number' ? initial : 0;

  // Rien à proposer du tout, et rien à y faire : ce n'est pas une recherche
  // infructueuse, c'est que la relation n'existe pas. Le dire explicitement
  // évite de laisser croire à une liste qui met du temps à charger.
  const nothingToOffer = schema.options.length === 0;

  const notice = (key: string): string =>
    `<span style="grid-column:1 / -1;color:var(--text3);font-size:11px">${esc(t(key))}</span>`;

  const renderList = (filter: string): string => {
    if (nothingToOffer) return notice('workspace.param.empty.' + schema.type);
    const pool = schema.options.filter(
      (o) =>
        !filter ||
        (o.name || '').toLowerCase().includes(filter) ||
        (o.code || '').toLowerCase().includes(filter),
    );
    if (!pool.length) return notice('workspace.param.noResults');
    return pool
      .map(
        (o) =>
          `<label style="display:flex;align-items:center;gap:4px;font-size:11px;cursor:pointer;min-width:0">` +
          `<input type="radio" name="ws-param" class="ws-param-r" value="${o.id}"${
            o.id === picked ? ' checked' : ''
          }>` +
          `<span title="${esc(o.name || '')}" style="font-size:10px;color:var(--text3);flex-shrink:0;cursor:help">${esc(
            o.code || '—',
          )}</span>` +
          `<span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0">${esc(
            truncate(o.name || ''),
          )}</span></label>`,
      )
      .join('');
  };

  openModal(
    t('workspace.tile.' + entry.key + '.title'),
    `<label class="form-label" for="ws-param-search">${esc(
      t('workspace.param.' + schema.type),
    )}</label>
     ${
       // Une recherche au-dessus d'une liste vide n'a rien à chercher.
       nothingToOffer
         ? ''
         : `<input type="text" id="ws-param-search" class="form-control" autocomplete="off"
       placeholder="${esc(t('workspace.param.search.' + schema.type))}">`
     }
     <div id="ws-param-picker" style="max-height:220px;overflow-y:auto;margin-top:8px;
       display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:3px 10px">${renderList(
         '',
       )}</div>`,
    () => {
      // Sans choix, la tuile serait refusée par le serveur (paramètre requis)
      // : on laisse la popup ouverte plutôt que d'aller chercher un 422.
      if (!picked) return;
      // `openModal` ne referme rien de lui-même : c'est au callback de le
      // faire. Fermé AVANT l'enregistrement, pour que la popup ne reste pas
      // ouverte le temps de l'aller-retour ; `persist` restaure et prévient
      // si le serveur refuse.
      closeModal();
      const tiles = [...store.getTiles()];
      if (existing) {
        const idx = tiles.findIndex((tj) => tj.id === existing.id);
        const target = tiles[idx];
        if (target) tiles[idx] = { ...target, params: { [schema.name]: picked } };
      } else {
        tiles.push(newTile(entry, { [schema.name]: picked }));
      }
      return persist(tiles);
    },
    t('common.save'),
  );

  const picker = document.getElementById('ws-param-picker');
  picker?.addEventListener('change', (ev) => {
    const radio = ev.target as HTMLInputElement | null;
    if (radio?.classList.contains('ws-param-r')) picked = Number(radio.value);
  });
  document.getElementById('ws-param-search')?.addEventListener('input', (ev) => {
    const field = ev.target as HTMLInputElement | null;
    if (picker) picker.innerHTML = renderList((field?.value ?? '').toLowerCase().trim());
  });
}

/** Catalogue : une tuile paramétrable peut être ajoutée autant de fois que voulu. */
async function openCatalog(): Promise<void> {
  const catalog = store.getCatalog();
  const placed = new Set(store.getTiles().map((tj) => tj.key));
  const items = catalog
    .map((entry) => {
      const parametrable = entry.params_schema.length > 0;
      // Une tuile SANS paramètre n'a aucune raison d'être posée deux fois :
      // le catalogue la grise une fois posée. Une tuile paramétrable, si.
      const disabled = !parametrable && placed.has(entry.key);
      return `<li class="ws-cat-item">
        <div>
          <b>${esc(t('workspace.tile.' + entry.key + '.title'))}</b>
          <p class="ws-cat-d">${esc(t('workspace.tile.' + entry.key + '.help'))}</p>
        </div>
        <button type="button" class="btn btn-secondary" data-add="${esc(entry.key)}"
          ${disabled ? 'disabled' : ''}>
          ${esc(disabled ? t('workspace.placed') : t('workspace.addOne'))}
        </button>
      </li>`;
    })
    .join('');
  openWideModal(
    t('workspace.catalogTitle'),
    `<ul class="ws-cat">${items}</ul>`,
    // Même règle que le formulaire de paramètres : `openWideModal` ne referme
    // pas de lui-même, et « Fermer » est ici la seule sortie de la modale avec
    // la croix (`hideCancel`).
    () => closeModal(),
    t('common.close'),
    true,
  );
  document.querySelectorAll<HTMLElement>('[data-add]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const entry = catalog.find((c) => c.key === btn.dataset.add);
      if (!entry) return;
      if (entry.params_schema.length) {
        void closeModalSafe().then(() => openParams(entry));
        return;
      }
      const tiles = [...store.getTiles(), newTile(entry, {})];
      void closeModalSafe().then(() => persist(tiles));
    });
  });
}

/**
 * Ouvre le formulaire de paramètres d'une tuile déjà posée, d'où qu'on clique.
 *
 * Le volet rend les mêmes tuiles que la page, donc la même roue « Paramétrer »
 * — mais sa délégation de clics ne connaissait qu'un geste, l'atterrissage :
 * la roue y menait vers la liste filtrée au lieu d'ouvrir le formulaire.
 */
export function openTileParams(tileId: number): void {
  const tile = store.getTiles().find((tj) => tj.id === tileId);
  const entry = store.getCatalog().find((c) => c.key === tile?.key);
  if (tile && entry) void openParams(entry, tile);
}

/**
 * Instance non encore persistée. `id: 0` signale au PUT qu'il s'agit d'une
 * création — c'est le serveur qui attribue l'identifiant, et c'est lui qui le
 * fait survivre aux réordonnancements suivants.
 */
function newTile(entry: CatalogEntry, params: Record<string, unknown>): WorkspaceTile {
  return { id: 0, key: entry.key, size: entry.sizes[0] || '1x1', params } as WorkspaceTile;
}

// ── Câblage ────────────────────────────────────────────────────────────────

function wire(): void {
  document.getElementById('ws-add')?.addEventListener('click', () => void openCatalog());
  document.getElementById('ws-reorder')?.addEventListener('click', () => {
    reorderMode = !reorderMode;
    paint();
  });
  document.getElementById('ws-reset')?.addEventListener('click', resetLayout);

  const grid = document.querySelector<HTMLElement>('.ws-grid');
  if (!grid) return;

  // Les tuiles portent `draggable` depuis l'origine ; il leur manquait le
  // câblage, donc le glisser ne produisait rien — une invite qui ne mène nulle
  // part. Les flèches ↑↓ du mode « Réorganiser » restent le chemin accessible
  // et tactile, elles ne sont pas remplacées par celui-ci.
  initDragOrder({
    container: grid,
    itemSelector: '.ws-tile',
    idKey: 'tile',
    onReorder: reorderTo,
  });

  grid.addEventListener('click', (ev) => {
    const el = ev.target as HTMLElement | null;
    if (!el) return;

    const help = el.closest<HTMLElement>('.ws-help');
    if (help?.dataset.tileKey) return openHelp(help.dataset.tileKey);

    const move2 = el.closest<HTMLElement>('.ws-move');
    if (move2?.dataset.tile) {
      return move(Number(move2.dataset.tile), move2.dataset.dir === 'up' ? 'up' : 'down');
    }

    const rm = el.closest<HTMLElement>('.ws-remove');
    if (rm?.dataset.tile) return remove(Number(rm.dataset.tile));

    const gear = el.closest<HTMLElement>('.ws-gear');
    if (gear?.dataset.tile) return openTileParams(Number(gear.dataset.tile));

    if (reorderMode) return;

    const kpi = el.closest<HTMLElement>('.ws-kpi');
    const bar = el.closest<HTMLElement>('.ws-bar');
    const week = el.closest<HTMLElement>('.ws-wk');
    const land = el.closest<HTMLElement>('.ws-land');
    const card = el.closest<HTMLElement>('.ws-tile');
    if (!card?.dataset.tile) return;
    const tile = store.getTiles().find((tj) => tj.id === Number(card.dataset.tile));
    if (!tile) return;

    if (kpi) {
      const facet = kpi.dataset.facet;
      return router.navigate(landingHash(tile, facetForKpi(facet), facet));
    }
    if (bar?.dataset.status)
      return router.navigate(landingHash(tile, { status_id: bar.dataset.status }));
    if (week) {
      const from = week.dataset.dueFrom || '';
      const to = week.dataset.dueTo || '';
      return router.navigate(
        landingHash(tile, from && to ? { due_from: from, due_to: to } : { late_only: 'true' }),
      );
    }
    if (land) return router.navigate(landingHash(tile));
  });
}

// ── Page ───────────────────────────────────────────────────────────────────

// Le store bouge aussi sous la page : réconciliation après une mutation, ou
// décrémentation optimiste déclenchée par le volet. Sans ce repeint, le
// clignotement ne se verrait que dans le volet, jamais sur la page elle-même.
// Le garde sur le hash est indispensable : un abonnement de module survit à la
// navigation, et `setContent` écraserait la page d'à côté.
store.subscribe(() => {
  if (!store.isLoaded()) return;
  if (!location.hash.startsWith('#workspace')) return;
  paint();
});

pages.workspace = async function () {
  setHeader(t('workspace.title'), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    await store.load();
    paint();
  } catch (e) {
    safeWarn('[workspace] load', e);
    setContent(`<div class="error">${esc(t('workspace.loadFailed'))}</div>`);
  }
};
